/**
 * usePrintAgent — Print Agent connectivity + print commands
 *
 * Composes useAgentPort to discover the agent's port (BLU-410 — was hardcoded
 * 9876, replaced by 5-port discovery). Once discovered, polls /status every
 * 30s for capability + update info, and exposes print() / printLabel().
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
  parsePrinterFormat,
  toPrintErrorCode,
  type PrintErrorCode,
  type PrinterFormat,
} from '@threadheads/shared';

import { resolveAgentPort, useAgentPort } from './useAgentPort';

const PROBE_TIMEOUT_MS = 2000;

interface AgentStatus {
  ok: boolean;
  version?: string;
  port?: number;
  // Process fingerprint (BLU single-instance): pid + per-boot instance id.
  pid?: number;
  instanceId?: string;
  capabilities?: string[];
  labelPrinter?: string;
  updateAvailable?: boolean;
  latestVersion?: string | null;
  lastUpdateCheck?: string | null;
  /**
   * BLU-1336: this PC's declared printer format, sent only when declared.
   * Untrusted until it passes `parsePrinterFormat`.
   */
  printerFormat?: unknown;
  /**
   * Agent 2.0.23+ (staging only): which of this agent's drop folders are the
   * SAME folder the other flavour's agent uses ('print' | 'film'). Absent on
   * older agents and when nothing is shared.
   */
  sharedDropFolders?: unknown;
}

/** Keep only the folder kinds we know; anything else is ignored. */
function parseSharedDropFolders(raw: unknown): Array<'print' | 'film'> {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is 'print' | 'film' => v === 'print' || v === 'film');
}

interface PrintRequest {
  sku: string;
  dualSide?: 'front' | 'back';
  artworkBarcode?: string;
  /**
   * BLU-368: optional location uuid. When the agent has BACKEND_URL +
   * PRINT_AGENT_API_KEY configured, supplying this routes the print
   * request through the backend so multi-tenant + Tencent COS locations
   * resolve correctly. When omitted (or the agent lacks backend env),
   * the agent uses its existing local Azure-Tables lookup. Optional so
   * the packing-station label flow doesn't need to pass it.
   */
  locationId?: string;
}

/**
 * Structured print-failure classification, mirrored from the print-agent
 * sidecar's `/print` response (apps/print-agent/src/index.ts). The agent is
 * NOT a workspace member, so this union is kept aligned by hand — keep the two
 * in sync. `agent_unreachable` is frontend-only (the sidecar cannot report its
 * own absence).
 */
export type PrintFailureReason =
  | 'artwork_missing'
  | 'storage_unreachable'
  | 'credentials'
  | 'rate_limited'
  | 'agent_unreachable'
  // File downloaded fine but the LOCAL print app (RIP / Invoke-Item) couldn't
  // open it — distinct from "couldn't reach the file". Sidecar-reported.
  | 'print_open_failed'
  // Download succeeded but writing the file to the local cache failed — the
  // operator machine's disk is full (ENOSPC). Sidecar-reported.
  | 'disk_full'
  | 'unknown';

interface PrintResult {
  success: boolean;
  message?: string;
  cached?: boolean;
  /** Phase 2: structured failure classification — present only when success=false. */
  reason?: PrintFailureReason;
  /** Phase 2: whether re-firing the same print is worth attempting. */
  retryable?: boolean;
  /** Specific shared PrintErrorCode — drives the precise operator message. */
  code?: PrintErrorCode;
  /** Pipeline stage the failure occurred at (resolve/download/open/connectivity). */
  stage?: string;
  /** Backend/storage HTTP status, when the failure had one. */
  httpStatus?: number;
  /**
   * Correlation id for this print attempt — shown to the operator as a short
   * ref and attached to any reported issue so a dev can trace it in
   * Sentry/Railway without opening the print station's local log.
   */
  correlationId?: string;
  /**
   * BLU-1336: the sidecar's finer failure detail, when it sends one (e.g.
   * `unsupported_extension` when a Brother driver is handed a file it cannot
   * take). Drives the print station's printer file-type wording.
   */
  detailCode?: string;
}

interface PrintLabelRequest {
  data: string;
  format: string;
  copies?: number;
}

interface PrintLabelResult {
  success: boolean;
  error?: string;
}

export function usePrintAgent() {
  const { port, status: portStatus, ignoredAgent, retry: retryPortDiscovery } = useAgentPort();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [version, setVersion] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [labelPrinter, setLabelPrinter] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  // BLU-1336: the PC's declared printer format. Only a good probe (HTTP OK
  // and `ok: true`) writes it, so a flaky probe never flaps it to null and
  // re-arms a stop. Cleared on `not_found` and on a real port change.
  const [printerFormat, setPrinterFormat] = useState<PrinterFormat | null>(null);
  const [sharedDropFolders, setSharedDropFolders] = useState<Array<'print' | 'film'>>([]);
  const lastBaseUrlRef = useRef<string | null>(null);
  // BLU-1336: the probe armed by the current effect run, so
  // `confirmPrinterFormat` can re-read `/status` once on demand.
  const probeRef = useRef<(() => Promise<PrinterFormat | null>) | null>(null);

  const baseUrl = port !== null ? `http://127.0.0.1:${port}` : null;

  // Probe agent on mount + every 30s once we have a port. Per-effect
  // `cancelled` flag (not a shared ref) so portStatus changes don't suppress
  // setIsLoading(false) on the in-flight effect's finally — the prior shared
  // mountedRef flipped false on dependency change and stuck isLoading at true.
  useEffect(() => {
    let cancelled = false;

    if (portStatus === 'discovering') {
      // Hold isLoading until discovery resolves; nothing to probe yet.
      setIsLoading(true);
      return () => {
        cancelled = true;
      };
    }

    if (portStatus === 'not_found' || baseUrl === null) {
      // Discovery exhausted — surface the disconnected state immediately.
      setIsConnected(false);
      setVersion(null);
      setCapabilities([]);
      setLabelPrinter(null);
      setUpdateAvailable(false);
      setLatestVersion(null);
      setPrinterFormat(null);
      setSharedDropFolders([]);
      lastBaseUrlRef.current = null;
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    // A different agent port means a different agent: its declaration is
    // unknown until its own first good probe.
    if (lastBaseUrlRef.current !== null && lastBaseUrlRef.current !== baseUrl) {
      setPrinterFormat(null);
    }
    lastBaseUrlRef.current = baseUrl;

    // Resolves the format this probe just read (null on any failure or a
    // cancelled run).
    const probe = async (): Promise<PrinterFormat | null> => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

        const res = await fetch(`${baseUrl}/status`, {
          signal: controller.signal,
        });
        clearTimeout(timeout);

        if (cancelled) return null;

        if (res.ok) {
          const data: AgentStatus = await res.json();
          setIsConnected(data.ok);
          setVersion(data.version ?? null);
          setCapabilities(data.capabilities ?? []);
          setLabelPrinter(data.labelPrinter ?? null);
          setUpdateAvailable(data.updateAvailable ?? false);
          setLatestVersion(data.latestVersion ?? null);
          setSharedDropFolders(parseSharedDropFolders(data.sharedDropFolders));
          if (data.ok === true) {
            // A good undeclared probe overwrites with null on purpose.
            const format = parsePrinterFormat(data.printerFormat);
            setPrinterFormat(format);
            return format;
          }
          return null;
        } else {
          setIsConnected(false);
          setCapabilities([]);
          setLabelPrinter(null);
          setUpdateAvailable(false);
          setLatestVersion(null);
          return null;
        }
      } catch (err) {
        // Log probe failures so we can diagnose mixed-content / connectivity issues
        console.warn('[PrintAgent] Probe failed:', err instanceof Error ? err.message : err);
        if (!cancelled) {
          setIsConnected(false);
          setCapabilities([]);
          setLabelPrinter(null);
          setUpdateAvailable(false);
          setLatestVersion(null);
        }
        return null;
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    probeRef.current = probe;
    void probe();

    // Re-probe every 30 seconds
    const interval = setInterval(probe, 30_000);

    return () => {
      cancelled = true;
      probeRef.current = null;
      clearInterval(interval);
    };
  }, [baseUrl, portStatus]);

  /**
   * BLU-1336: re-read this PC's `/status` once (same 2 s timeout, same state
   * writes) and resolve the fresh declared format, or null when the probe
   * fails, the PC is undeclared or no agent port is armed. Called only right
   * before the print station stops a print, so a re-declared PC never gets a
   * stale stop. No listener, no extra interval.
   */
  const confirmPrinterFormat = useCallback(async (): Promise<PrinterFormat | null> => {
    const probe = probeRef.current;
    return probe ? probe() : null;
  }, []);

  const print = useCallback(async (request: PrintRequest): Promise<PrintResult> => {
    if (!baseUrl) {
      return {
        success: false,
        message: 'Print agent not reachable. Check Settings > Production > Print Agent',
        reason: 'agent_unreachable',
        retryable: true,
        code: 'agent_unreachable',
      };
    }
    setIsPrinting(true);
    try {
      const res = await fetch(`${baseUrl}/print`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      const data = await res.json();

      if (!res.ok) {
        return {
          success: false,
          message: data.error || 'Print failed',
          reason: (data.reason as PrintFailureReason | undefined) ?? 'unknown',
          retryable: typeof data.retryable === 'boolean' ? data.retryable : true,
          // Specific code (normalised — older agents may omit it → 'unknown'),
          // plus the stage/status/corr id the sidecar now returns.
          code: toPrintErrorCode(data.code),
          stage: typeof data.stage === 'string' ? data.stage : undefined,
          httpStatus: typeof data.httpStatus === 'number' ? data.httpStatus : undefined,
          correlationId: typeof data.correlationId === 'string' ? data.correlationId : undefined,
          detailCode: typeof data.detailCode === 'string' ? data.detailCode : undefined,
        };
      }

      return {
        success: true,
        message: data.message,
        cached: data.cached,
      };
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : 'Failed to connect to print agent',
        reason: 'agent_unreachable',
        retryable: true,
        code: 'agent_unreachable',
      };
    } finally {
      setIsPrinting(false);
    }
  }, [baseUrl]);

  const printLabel = useCallback(async (request: PrintLabelRequest): Promise<PrintLabelResult> => {
    // A label fired the moment a screen mounts (packer auto-print) can beat
    // this hook's scan; wait for it rather than report "not reachable".
    const labelPort = port ?? (await resolveAgentPort());
    const labelUrl = labelPort !== null ? `http://127.0.0.1:${labelPort}` : null;
    if (!labelUrl) {
      // BLU-429: capture as a warning so we see fleet-wide which PCs have
      // no reachable agent. Operator-impact is recoverable (browser print
      // fallback in LabelPrintDialog), hence warning not error.
      Sentry.captureMessage('print-label requested but agent not reachable', {
        level: 'warning',
        tags: { surface: 'usePrintAgent.printLabel.no-baseUrl' },
        extra: {
          format: request.format,
          dataLength: request.data.length,
          copies: request.copies ?? 1,
        },
      });
      return {
        success: false,
        error: 'Print agent not reachable. Check Settings > Production > Print Agent',
      };
    }
    try {
      const res = await fetch(`${labelUrl}/print-label`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      const data = await res.json();

      if (!res.ok) {
        // BLU-429: capture HTTP failures so we have fleet-wide visibility
        // on the 501 ("Label printing not configured") + 500 ("SumatraPDF
        // failed") paths. Sidecar captures these too (PR-A on the sidecar
        // side), but capturing on BOTH sides lets us correlate via order
        // ID / location ID context that's only available in the frontend.
        Sentry.captureMessage('print-label failed (agent returned non-2xx)', {
          level: 'warning',
          tags: {
            surface: 'usePrintAgent.printLabel.http-error',
            http_status: String(res.status),
            label_printer_configured: labelPrinter ? 'yes' : 'no',
          },
          extra: {
            sidecarPort: labelPort,
            agentVersion: version,
            labelPrinterName: labelPrinter,
            httpStatus: res.status,
            agentError: data?.error ?? 'unknown',
          },
        });
        return { success: false, error: data.error || 'Label print failed' };
      }

      return { success: true };
    } catch (err) {
      // Network-level failure (sidecar process gone, port collision, etc).
      // Capture with the error itself for stack-trace richness.
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: { surface: 'usePrintAgent.printLabel.network-error' },
        extra: {
          sidecarPort: labelPort,
          agentVersion: version,
          labelPrinterName: labelPrinter,
        },
      });
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to connect to print agent',
      };
    }
  }, [port, version, labelPrinter]);

  const canPrintLabels = isConnected && capabilities.includes('label');

  return {
    isConnected,
    isLoading,
    isPrinting,
    version,
    capabilities,
    canPrintLabels,
    labelPrinter,
    updateAvailable,
    latestVersion,
    print,
    printLabel,
    /** BLU-1336: this PC's declared printer format (kept across failed probes). */
    printerFormat,
    /** BLU-1336: one fresh `/status` read; resolves the confirmed format or null. */
    confirmPrinterFormat,
    /** BLU-410: bound port (null while discovering / not found). */
    port,
    /** BLU-410: discovery status — `discovering`, `connected`, or `not_found`. */
    portStatus,
    /** BLU-410: clear cache + re-run discovery (call after the operator sets a manual override). */
    retryPortDiscovery,
    /** An agent of the other flavour this site skipped (settings notice), or null. */
    ignoredAgent: ignoredAgent ?? null,
    /** Drop folders this agent shares with the other flavour's agent (settings warning). */
    sharedDropFolders,
  };
}
